/**
 * MODULE: AssistantQuotaExceededBanner.tsx
 * PURPOSE: Proactive composer banner for AI token quota warnings and blocks
 * PUBLIC_API: AssistantQuotaExceededBanner, AssistantQuotaSettingsLink
 * AGENT_SAFETY: SAFE
 */
import { useNavigate } from 'react-router-dom';

import {
  ASSISTANT_QUOTA_BILLING_PATH,
  ASSISTANT_QUOTA_SETTINGS_PATH,
  assistantQuotaBannerCopyForSeverity,
  type AssistantQuotaSeverity,
} from './assistantQuotaGuidance';

export type AssistantQuotaExceededBannerProps = {
  onDismiss: () => void;
  className?: string;
  severity?: AssistantQuotaSeverity;
  percentUsed?: number;
};

const SHELL: Record<AssistantQuotaSeverity, string> = {
  warning:
    'border-amber-300 bg-amber-50 text-amber-950 dark:border-amber-700 dark:bg-amber-950/30 dark:text-amber-100',
  critical:
    'border-orange-400 bg-orange-50 text-orange-950 dark:border-orange-700 dark:bg-orange-950/40 dark:text-orange-100',
  exceeded:
    'border-red-400 bg-red-50 text-red-950 dark:border-red-700 dark:bg-red-950/30 dark:text-red-100',
};

export function AssistantQuotaExceededBanner({
  onDismiss,
  className = 'mb-2',
  severity = 'exceeded',
  percentUsed,
}: AssistantQuotaExceededBannerProps) {
  const navigate = useNavigate();
  const testId =
    severity === 'exceeded' ? 'assistant-quota-exceeded-banner' : 'assistant-quota-warning-banner';

  return (
    <div
      data-testid={testId}
      data-quota-severity={severity}
      className={`flex flex-col gap-2 rounded-lg border px-3 py-2 text-xs ${SHELL[severity]} ${className}`}
      role="status"
    >
      <p>{assistantQuotaBannerCopyForSeverity(severity, percentUsed)}</p>
      <div className="flex flex-wrap items-center gap-2">
        <button
          type="button"
          onClick={() => navigate(ASSISTANT_QUOTA_SETTINGS_PATH)}
          className="rounded-md border border-current/30 bg-white/80 px-2 py-1 text-xs font-medium hover:bg-white dark:bg-black/20 dark:hover:bg-black/40"
        >
          Review AI usage
        </button>
        <button
          type="button"
          onClick={() => navigate(ASSISTANT_QUOTA_BILLING_PATH)}
          className="rounded-md border border-current/30 bg-white/80 px-2 py-1 text-xs font-medium hover:bg-white dark:bg-black/20 dark:hover:bg-black/40"
        >
          Top up plan
        </button>
        <button
          type="button"
          onClick={onDismiss}
          className="rounded-md px-2 py-1 text-xs font-medium opacity-80 hover:underline"
        >
          Dismiss
        </button>
      </div>
    </div>
  );
}

export type AssistantQuotaSettingsLinkProps = {
  className?: string;
};

/** Compact CTA for inline error bubbles (matches Langflow panel retry row). */
export function AssistantQuotaSettingsLink({ className }: AssistantQuotaSettingsLinkProps) {
  const navigate = useNavigate();

  return (
    <button
      type="button"
      onClick={() => navigate(ASSISTANT_QUOTA_SETTINGS_PATH)}
      className={
        className ??
        'inline-flex items-center gap-1 rounded-lg border border-amber-300 bg-amber-50 px-2 py-1 text-xs font-medium text-amber-950 shadow-sm hover:bg-amber-100 dark:border-amber-700 dark:bg-amber-950/40 dark:text-amber-100 dark:hover:bg-amber-950/60'
      }
      data-testid="assistant-quota-settings-link"
    >
      Top up in Settings
    </button>
  );
}
